import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMyDevices } from '../../hooks/useMyDevices';
import { estimateBatteryLife } from '../../lib/batteryLife';
import { SearchBar } from '../ui/SearchBar';
import { NotificationBell } from '../ui/NotificationBell';
import { Avatar } from '../ui/Avatar';

export function Header() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const myDevices = useMyDevices();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const swapCount = (myDevices.data ?? []).filter(
    (d) => estimateBatteryLife(d.batteryHealthPct, d.purchasedAt).tone === 'critical'
  ).length;

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="border-b border-surface-600 bg-surface-900 sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-1 shrink-0">
          <span className="text-lime-400 text-xl font-bold">battery</span>
          <span className="text-neutral-100 text-xl font-bold">.cafe</span>
        </Link>

        <SearchBar className="max-w-md" />

        <nav className="flex items-center gap-2 shrink-0 ml-auto">
          {isAuthenticated ? (
            <>
              <NotificationBell count={swapCount} />
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Account menu"
                  aria-expanded={menuOpen}
                >
                  <Avatar name={user?.displayName || user?.email} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 panel p-2 text-sm">
                    <div className="px-3 py-2 text-ink-500 truncate">
                      {user?.displayName || user?.email}
                    </div>
                    <Link
                      to="/me/devices"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg hover:bg-ink-900/5"
                    >
                      My devices
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-ink-900/5"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/register"
                className="hidden sm:inline text-sm text-neutral-300 hover:text-lime-400 whitespace-nowrap"
              >
                Register to view your devices
              </Link>
              <Link to="/login" aria-label="Sign in">
                <Avatar />
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
